import { yupResolver } from '@hookform/resolvers/yup';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VIEW_NAME_MAX_LENGTH } from 'src/constants/common';
import * as yup from 'yup';

import CommonModal from '../CommonModal';
import InputText from '../InputText/InputText';

import { ICreateViewForm, ICreateViewModal } from './TableTabButton.interface';

const CreateViewModal: FC<ICreateViewModal> = ({ handleClose, handleSave, isLoading, title, primaryBtnText, viewNames, modalData, isError, setIsError }) => {
    const { t } = useTranslation();

    const schema = yup.object().shape({
        name: yup
            .string()
            .required(t('QuotingHub.QuotingDashboard.View_name_is_required'))
            .test('required', t('QuotingHub.QuotingDashboard.View_name_is_required'), (value) => value?.trim().length > 0)
            .max(VIEW_NAME_MAX_LENGTH, t('QuotingHub.QuotingDashboard.View_name_must_be_at_most_40_characters')),
    });

    const defaultValues = useMemo(
        () => ({
            name: modalData?.rename ? modalData?.tabName : '',
        }),
        [modalData]
    );

    const methods = useForm<ICreateViewForm>({
        resolver: yupResolver(schema) as unknown as Resolver<ICreateViewForm>,
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        setError,
        watch,
        formState: { isValid },
    } = methods;
    const viewName = watch('name');

    useEffect(() => {
        if (isError) {
            setError('name', {
                message: t('QuotingHub.QuotingDashboard.view_name_already_exists_Text'),
            });
        }
    }, [isError]);

    const handleChange = (onChange: any) => (e: any) => {
        onChange(e);
        setIsError(false);
    };

    const onSubmit = (data: ICreateViewForm) => {
        const trimmedName = data.name.trim();
        const trimmedLowercaseName = trimmedName.toLowerCase();

        // Check for special characters
        const hasSpecialCharacters = !/^[a-zA-Z0-9\s]+$/.test(trimmedName);

        if (hasSpecialCharacters) {
            setError('name', {
                message: t('QuotingHub.QuotingDashboard.View_name_no_special_characters'),
            });

            return;
        }

        // Check for duplicate names
        const isDuplicate = viewNames.some((name: string) => name.trim().toLowerCase() === trimmedLowercaseName);

        if (isDuplicate) {
            setError('name', {
                message: t('QuotingHub.QuotingDashboard.view_name_already_exists_Text'),
            });

            return;
        }

        const formData = {
            ...data,
            ...modalData,
            name: trimmedName,
        };
        handleSave(formData);
    };

    const memorizedBadge = useMemo(
        () => (
            <div className="text-grayLight900 text-xs">
                {viewName?.length}
                &nbsp;/&nbsp;40
            </div>
        ),
        [viewName]
    );

    const isFormDirty = useMemo(() => viewName?.trim()?.toLowerCase() !== modalData?.tabName?.trim()?.toLowerCase(), [viewName, modalData]);

    return (
        <CommonModal
            title={title}
            handleClose={handleClose}
            size={'max-w-[600px]'}
            modalClassName="flex flex-col"
            secondaryBtnText="Cancel"
            primaryBtnText={primaryBtnText}
            secondaryBtnOnClick={handleClose}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            primaryBtnDisabled={!isValid || !isFormDirty}
            primaryBtnLoading={isLoading}
        >
            <div className="p-4">
                <Controller
                    name="name"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <InputText
                            parentClassName="!mb-0"
                            label={t('QuotingHub.QuotingDashboard.Name')}
                            inputName="name"
                            placeholder={t('QuotingHub.QuotingDashboard.Enter_view_name')}
                            labelClassName="!mb-0"
                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                            labelWrapClass="!mb-1.5"
                            labelIconRight={<p className="text-textSecondary text-xs font-medium">{memorizedBadge}</p>}
                            value={value}
                            required
                            onChangeFunc={handleChange(onChange)}
                            errorText={error?.type !== 'required' && error?.message ? error?.message : ''}
                            isIconError={error?.type !== 'required' && !!error?.message}
                            maxLength={VIEW_NAME_MAX_LENGTH}
                            autoFocus={true}
                        />
                    )}
                />
            </div>
        </CommonModal>
    );
};

export default CreateViewModal;
